import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import {
  AgbChangeByOwnership,
  AgbChangeOwnershipRow,
} from '../lib/types';

// CSV schema (long format, one row per ownership_class plus an `all_private`
// rollup row produced by scripts/generate-agb-change-data.py):
//
//   ownership_class,total_acres,mean_change_mg_ha,
//     loss_acres,stable_acres,gain_acres,
//     large_loss_acres,loss_only_acres,stable_only_acres,
//     gain_only_acres,large_gain_acres
//
// `loss_acres` / `stable_acres` / `gain_acres` are the 3-class rollup
// (loss = large_loss + loss; gain = gain + large_gain). The five
// `*_only_acres` columns preserve the fine-grained 5-bin breakdown so the
// card can paint the diverging legend if it needs the full spectrum.

const ALL_PRIVATE_KEY = 'all_private';

const num = (s: string | undefined): number => {
  const v = Number(s);
  return Number.isFinite(v) ? v : 0;
};

const parseRow = (row: Record<string, string>): AgbChangeOwnershipRow => ({
  ownershipClass:    row['ownership_class'] ?? '',
  totalAcres:        num(row['total_acres']),
  meanChangeMgPerHa: num(row['mean_change_mg_ha']),
  lossAcres:         num(row['loss_acres']),
  stableAcres:       num(row['stable_acres']),
  gainAcres:         num(row['gain_acres']),
  largeLossAcres:    num(row['large_loss_acres']),
  lossOnlyAcres:     num(row['loss_only_acres']),
  stableOnlyAcres:   num(row['stable_only_acres']),
  gainOnlyAcres:     num(row['gain_only_acres']),
  largeGainAcres:    num(row['large_gain_acres']),
});

const parseAgbChangeByOwnership = (text: string): AgbChangeByOwnership | null => {
  const rows = parseCsv(text).map(parseRow);
  if (rows.length === 0) return null;

  const allPrivate =
    rows.find((r) => r.ownershipClass === ALL_PRIVATE_KEY) ?? null;

  const byClass: Record<string, AgbChangeOwnershipRow> = {};
  for (const r of rows) {
    if (r.ownershipClass !== ALL_PRIVATE_KEY) byClass[r.ownershipClass] = r;
  }

  return { rows, allPrivate, byClass };
};

/**
 * Fetches the per-ownership AGB change crosstab CSV for the supply shed
 * defined by (millCode, driveTime). Lives on S3 at
 * `sfi/agb/change/{mill}_{dt}min_change_by_ownership.csv`. The CSV is the
 * sibling of the AGB change PNG — they're produced together by
 * `scripts/generate-agb-change-data.py` so a 404 here means the precompute
 * hasn't been run for that shed yet.
 */
export const useAgbChangeByOwnership = createCsvStatsHook({
  path: API_CONFIG.s3.paths.agbChangeByOwnership,
  parse: parseAgbChangeByOwnership,
  errorPrefix: 'Failed to load AGB change crosstab',
});
